"use client";

import Link from "next/link";
import type { Route } from "next";
import { useLayoutEffect, useState } from "react";
import type { ReactNode } from "react";

import { BLOG_SEARCH_RESULT_ORIGIN_DATASET_KEY } from "@/lib/blog/search-navigation";

type SearchResultCardLinkProps = {
  action: string;
  children: ReactNode;
  className: string;
  href: string;
  label?: string;
  queryLength: number;
  resultRank?: number | null;
  searchBucket?: string;
  source: string;
  tool: string;
  value: string;
  value2?: string;
};

function readSearchResultOrigin() {
  if (typeof document === "undefined") return undefined;
  const value =
    document.documentElement.dataset[BLOG_SEARCH_RESULT_ORIGIN_DATASET_KEY];
  return typeof value === "string" && value.trim().length > 0
    ? value
    : undefined;
}

export default function SearchResultCardLink({
  action,
  children,
  className,
  href,
  label,
  queryLength,
  resultRank,
  searchBucket,
  source,
  tool,
  value,
  value2,
}: SearchResultCardLinkProps) {
  const [resultOrigin, setResultOrigin] = useState<string | undefined>(
    undefined
  );

  useLayoutEffect(() => {
    if (typeof document === "undefined") return;

    const sync = () => {
      setResultOrigin(readSearchResultOrigin());
    };

    sync();

    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  return (
    <Link<string>
      href={href as Route<string>}
      className={className}
      data-ga-action={action}
      data-ga-category="blog"
      data-ga-event="tool_search_click"
      data-ga-label={label}
      data-ga-query-length={String(queryLength)}
      data-ga-result-origin={resultOrigin}
      data-ga-result-rank={
        typeof resultRank === "number" ? String(resultRank) : undefined
      }
      data-ga-search-bucket={searchBucket}
      data-ga-source={source}
      data-ga-tool={tool}
      data-ga-value={value}
      data-ga-value2={value2}
    >
      {children}
    </Link>
  );
}
